import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { AppDispatch } from "../../redux/store";
import PermissionGate from "../../core/layouts/permission-gate";
import ToastComponent from "../../components/toast";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../redux/features/common-slice";
import Table from "../../components/table";
import TableBodyCell from "../../components/table-body-cell";
import EmptyDataOverlay from "../../components/empty-data-overlay";
import Loading from "../../components/loading";
import Chip from "../../components/chip";
import paths from "../../navigation/paths";
import {
  allIncidentsStateAction,
  getAllIncidentsState,
  Status,
  statusIcons,
  urgencyIcons,
} from "../../redux/features/incident-slice";
import { dateFormatter } from "../../core/utils";
import { getUserState } from "../../redux/features/auth-slice";

export default function IncidentsListPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const userState = useSelector(getUserState);
  const allIncidentsState = useSelector(getAllIncidentsState);
  const errorState = useSelector(getErrorState);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchAllIncidents = useCallback(async () => {
    if (!userState) {
      return;
    }
    try {
      await dispatch(allIncidentsStateAction());
      dispatch(
        setBreadcrumbState([
          { id: paths.INCIDENTS, path: null, title: "Incidents" },
          { id: "incidents", path: null, title: "All" },
        ])
      );
    } catch (error) {
      console.error(error);
      toast("Error fetching incidents", { type: "error" });
    }
  }, [dispatch]);

  useEffect(() => {
    if (userState) {
      fetchAllIncidents();
    }
  }, [fetchAllIncidents, userState]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  const filteredIncidents = allIncidentsState.data.filter((incident) => {
    const matchesTitle = incident.title
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCustomer = incident.customer?.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesTechnicalUnits = incident.technicalUnits?.some((unit) =>
      unit.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return matchesTitle || matchesCustomer || matchesTechnicalUnits;
  });

  return (
    <PermissionGate categories={[]} roles={[]}>
      <Helmet title={"All Incidents"} />
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by title, customer, or technical unit"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="p-2 border rounded w-1/4"
        />
      </div>
      <Table
        style={{
          maxHeight: "calc(100% - 64px)",
        }}
        headerCells={[
          {
            id: "title",
            label: "Title",
            align: "text-left",
            width: "w-2/12",
          },
          {
            id: "urgencyLevel",
            label: "Urgency Level",
            align: "text-left",
            width: "w-2/12",
          },
          {
            id: "technicalUnit",
            label: "Technical Unit",
            align: "text-left",
            width: "w-2/12",
          },
          {
            id: "customer",
            label: "Customer",
            align: "text-left",
            width: "w-2/12",
          },
          {
            id: "status",
            label: "Status",
            align: "text-left",
            width: "w-1/12",
          },
          {
            id: "created",
            label: "Date Logged",
            align: "text-left",
            width: "w-1/12",
          },
        ]}
        body={
          allIncidentsState.loading
            ? [
                <tr key="loading-row">
                  <td colSpan={6} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <Loading />
                    </div>
                  </td>
                </tr>,
              ]
            : filteredIncidents.length > 0
            ? filteredIncidents.map((incident) => {
                return (
                  <tr
                    key={incident.id}
                    className="border-t border-dashed border-divider cursor-pointer hover:bg-action-hover"
                    onClick={() =>
                      navigate(`${paths.INCIDENTS}/${incident.id}`)
                    }
                  >
                    <TableBodyCell exClassName="font-medium">
                      <div className="flex items-center space-x-2">
                        <label className="whitespace-normal break-words hover:underline cursor-pointer">
                          {incident.title}
                        </label>
                        {incident.status === Status.Unresolved && (
                          <Chip
                            label="Unresolved"
                            type="error"
                            exClassName="w-fit"
                          />
                        )}
                      </div>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words">
                        {urgencyIcons[incident.urgencyLevel]}{" "}
                        {incident.urgencyLevel}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words">
                        {incident.technicalUnits
                          ?.map((unit) => unit.name)
                          .join(", ") ?? ""}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words">
                        {incident.customer?.name ?? ""}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words">
                        {statusIcons[incident.status]} {incident.status}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words">
                        {dateFormatter(new Date(incident.created))}
                      </label>
                    </TableBodyCell>
                  </tr>
                );
              })
            : [
                <tr key="empty-row">
                  <td colSpan={6} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <EmptyDataOverlay
                        label="No incidents found"
                        exClassName="h-20"
                      />
                    </div>
                  </td>
                </tr>,
              ]
        }
      />
      <ToastComponent id="allIncidents" />
    </PermissionGate>
  );
}
